import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logDatabaseOperation, addBreadcrumb, logError } from "@/lib/logger";
import { Message } from "@/types/message";

const MESSAGES_PER_PAGE = 50;

export const useMessages = (userId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestMessageDate, setOldestMessageDate] = useState<string | null>(null);

  const loadMessages = useCallback(async (reset = true) => {
    if (!userId) return;
    
    try {
      addBreadcrumb('Loading messages', { user_id: userId, reset }, 'info', 'chat');
      
      const query = supabase
        .from("messages")
        .select("*")
        .eq("user_id", userId)
        .eq("deleted", false)
        .order("created_at", { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      const { data, error } = await query;

      if (error) {
        logDatabaseOperation('select', 'messages', false, error, { user_id: userId });
        toast.error("Failed to load messages");
        return;
      }

      logDatabaseOperation('select', 'messages', true, undefined, { 
        user_id: userId, 
        count: data?.length || 0 
      });

      const fetchedMessages = (data as Message[]) || [];
      
      if (reset) {
        setMessages(fetchedMessages.reverse());
        setOldestMessageDate(fetchedMessages[fetchedMessages.length - 1]?.created_at || null);
      }
      
      setHasMoreMessages(fetchedMessages.length === MESSAGES_PER_PAGE);
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: 'load_messages',
        user_id: userId,
      });
    }
  }, [userId]);

  const loadMoreMessages = useCallback(async () => {
    if (!oldestMessageDate || isLoadingMore || !hasMoreMessages || !userId) return;
    
    setIsLoadingMore(true);
    
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("user_id", userId)
      .eq("deleted", false)
      .lt("created_at", oldestMessageDate)
      .order("created_at", { ascending: false })
      .limit(MESSAGES_PER_PAGE);

    if (error) {
      toast.error("Failed to load more messages");
      setIsLoadingMore(false);
      return;
    }

    const fetchedMessages = (data as Message[]) || [];
    
    if (fetchedMessages.length > 0) {
      setMessages(prev => [...fetchedMessages.reverse(), ...prev]);
      setOldestMessageDate(fetchedMessages[fetchedMessages.length - 1]?.created_at || null);
      setHasMoreMessages(fetchedMessages.length === MESSAGES_PER_PAGE);
    } else {
      setHasMoreMessages(false);
    }
    
    setIsLoadingMore(false);
  }, [oldestMessageDate, isLoadingMore, hasMoreMessages, userId]);

  return {
    messages,
    setMessages,
    isLoadingMore,
    hasMoreMessages,
    loadMessages,
    loadMoreMessages,
  };
};
